import { useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { MyTileDeskMark, MyTileDeskWordmark } from '../../components/MyTileDeskMark.jsx'
import { Button, Field, PasswordInput, TextInput } from '../../components/ui/index.jsx'
import { useToast } from '../../components/ui/Toast.jsx'
import { useAuth } from '../../lib/auth.jsx'
import { ApprovalNotice } from './ApprovalStatus.jsx'
import './auth.css'

// A centred 400px card, outside the shell (§09a).
//
// It is also where a shop that registered finds out where its review stands
// (V013). The server answers a right password for a shop that is not approved
// with one of two codes, and those are said on the card — not in a toast — by
// <ApprovalNotice>, because they are not a mistake to correct.

const APPROVAL_CODES = {
  // V014: the password was right, the address is unproved, and the server has
  // just mailed a fresh link — the notice says where.
  EMAIL_NOT_VERIFIED: 'verify',
  TENANT_PENDING_APPROVAL: 'pending',
  TENANT_REJECTED: 'rejected',
  // Switched off by the platform: nobody in the shop signs in (2026-10-24).
  TENANT_SUSPENDED: 'suspended',
}

export default function LoginPage() {
  const { signIn } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const location = useLocation()

  // Filled in when the register page sends an approved owner here, so all that
  // is left to type is the password.
  const prefilled = location.state?.email ?? ''
  const [email, setEmail] = useState(prefilled)
  // No password state: the box is uncontrolled (see PasswordInput) and read
  // from the form on submit, so the password never reaches the HTML.
  const [fieldErrors, setFieldErrors] = useState({})
  const [busy, setBusy] = useState(false)
  // null, or { status, message } for a shop that has not been let in.
  const [approval, setApproval] = useState(null)

  async function onSubmit(event) {
    event.preventDefault()
    // Read before the first await: React clears currentTarget afterwards.
    const password = event.currentTarget.elements.password.value
    setBusy(true)
    setFieldErrors({})
    setApproval(null)
    try {
      const { justApproved } = await signIn(email, password)
      // The owner's first sign-in since their shop was approved: the shell
      // says so once, from navigation state, which is gone on the next reload.
      navigate(location.state?.from ?? '/', {
        replace: true,
        state: justApproved ? { approvedWelcome: true } : undefined,
      })
    } catch (error) {
      const status = APPROVAL_CODES[error?.code]
      if (status) {
        setApproval({ status, message: error.message })
        return
      }
      setFieldErrors(error?.fields ?? {})
      // message in the toast, fields under the inputs — never only one.
      toast.error(error?.message ?? 'Could not sign in.', error?.requestId)
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <MyTileDeskMark size={72} />
          <MyTileDeskWordmark size="md" />
        </div>

        {approval ? (
          <ApprovalNotice status={approval.status} message={approval.message}>
            {/* That refusal also left the registration cookie, so the register
                page can now follow this shop from this browser — and moves on
                by itself once the link is opened, on whatever device. */}
            {approval.status === 'verify' ? (
              <p>
                <Link to="/register">Follow your registration</Link>
              </p>
            ) : null}
          </ApprovalNotice>
        ) : null}

        <form onSubmit={onSubmit} noValidate>
          <div className="auth-fields">
            <Field label="Email" htmlFor="email" error={fieldErrors.email}>
              {({ describedBy }) => (
                <TextInput
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  autoFocus={!prefilled}
                  required
                  value={email}
                  describedBy={describedBy}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    // The notice is about the shop behind the address that was
                    // sent. Another address is another shop, or none.
                    setApproval(null)
                  }}
                />
              )}
            </Field>

            <Field label="Password" htmlFor="password" error={fieldErrors.password}>
              {({ describedBy }) => (
                <PasswordInput
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  autoFocus={Boolean(prefilled)}
                  required
                  describedBy={describedBy}
                />
              )}
            </Field>

            <Button type="submit" variant="primary" loading={busy}>
              Sign in
            </Button>
          </div>
        </form>

        <div className="auth-links">
          <Link to="/forgot">Forgot your password?</Link>
          <Link to="/register">Register a shop</Link>
        </div>
      </div>
    </main>
  )
}
